/**
 * @file Bandeau « Prochaines disponibilités » affiché quand le mois du calendrier n'a aucune place.
 * @module packages.common.components.table-reservation.components.NextAvailabilityBanner
 *
 * Le calendrier ne saute jamais de mois tout seul : un client qui atterrirait
 * sur novembre sans l'avoir demandé lirait « 25 » en pensant septembre. Ce
 * bandeau propose à la place les prochaines dates sous forme de cartes (jour,
 * numéro en grand, mois) ; le clic porte la date complète et c'est lui qui
 * déplace le calendrier. Seules les vraies places sont proposées, jamais la
 * liste d'attente. Horizon épuisé sans aucune place : aucune date n'est citée,
 * elle promettrait un après ; le texte dit que tout ce qui est ouvert est pris
 * (ou, si le serveur a plafonné son balayage, qu'il n'y a rien dans les N
 * prochains mois).
 *
 * Un mois sans place, qu'il soit complet ou fermé pour congés, appelle le même
 * bandeau : ce que le client veut savoir, c'est quand il pourra réserver. Seule
 * la date butoir fixe n'annonce pas d'ouverture, puisque c'est le restaurateur
 * qui ouvrira lui-même les dates suivantes.
 *
 * Carte plate sur le fond du panneau, filet neutre : le bandeau appartient au
 * calendrier, il ne doit pas ressembler à une alerte.
 *
 * Pendant la recherche, rien n'est affiché : ni squelette ni titre avant la
 * réponse, pour ne pas annoncer des « prochaines places » puis les remplacer
 * par une phrase qui dit le contraire. Le contenu final apparaît en fondu.
 *
 * Sans aucune place, le bloc devient un état vide compact, tout centré : la
 * ligne de sur-titre ne porte que l'icône entre ses deux filets (même grammaire
 * que « Prochaines disponibilités » et « Prochaine date à ouvrir »), puis la
 * phrase coupée en deux lignes équilibrées par un retour à la ligne porté par
 * la traduction. Pas de hauteur réservée : une phrase seule au milieu d'un grand
 * cadre vide ressemble à une erreur. Quand
 * le serveur annonce une prochaine ouverture, elle s'affiche dessous : le
 * sur-titre nomme la date qui va s'ouvrir, puis un libellé « Ouverture des
 * réservations » introduit l'instant d'ouverture (jour, heure, « dans N
 * jours », mêmes clés que l'infobulle du calendrier). Le mot « ouverture »
 * reste collé à l'instant d'ouverture, jamais au-dessus de la date cible.
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {format, parseISO, differenceInCalendarDays, startOfDay} from 'date-fns';

// @mui
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';

// icons
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import {NEXT_AVAILABILITY_STATUS} from '../hooks/useNextAvailability';
import {SERVICE_TYPES} from '../constants/reservation';

const LONG_DATE_FORMAT = 'EEEE d MMMM yyyy';
const DATE_CARD_WIDTH = 48;
const DATE_CARD_HEIGHT = 50;
const EMPTY_STATE_ICON_SIZE = 18;
const CONTENT_FADE_MS = 300;
// Durée approximative d'un mois pour traduire le dernier jour balayé en « N prochains mois ».
const DAYS_PER_MONTH = 30;

// Cartes de dates : filet neutre au repos, filet primaire au survol et au
// focus clavier.
const INTERACTIVE_SX = {
  'borderRadius': 1.5,
  'border': '1px solid',
  'borderColor': 'divider',
  'bgcolor': 'background.paper',
  'color': 'text.primary',
  'transition': (theme) => theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
  },
  '&.Mui-focusVisible': {
    borderColor: 'primary.main',
    boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
};

// Sur-titre centré entre deux filets fins, comme un séparateur de carte de
// restaurant : le filet porte la hiérarchie, pas la couleur. Partagé avec l'encart d'ouverture.
export const EYEBROW_SX = {
  'display': 'flex',
  'alignItems': 'center',
  'gap': 1.5,
  'typography': 'micro',
  'fontWeight': 700,
  'letterSpacing': '0.1em',
  'textTransform': 'uppercase',
  'color': 'text.secondary',
  'lineHeight': 1.4,
  'whiteSpace': 'nowrap',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: '1px',
    bgcolor: 'divider',
  },
};

/**
 * Bandeau des prochaines dates réservables, à monter au-dessus du calendrier.
 *
 * @param {Object} props
 * @param {string} props.status - Statut du chargement (`NEXT_AVAILABILITY_STATUS`)
 * @param {Object} [props.result] - Réponse `{dates, horizon}`
 * @param {Date} props.currentMonth - Mois affiché par le calendrier
 * @param {string} props.service - Service sélectionné (`SERVICE_TYPES`)
 * @param {number} props.partySize - Nombre de convives
 * @param {Function} props.onPickDate - Reçoit la date choisie 'YYYY-MM-DD'
 * @param {Object} [props.dateFnsLocale] - Locale date-fns courante
 * @param {Function} props.t - Fonction de traduction générique
 * @return {JSX.Element|null}
 */
export default function NextAvailabilityBanner({
  status,
  result,
  currentMonth,
  service,
  partySize,
  onPickDate,
  dateFnsLocale,
  t,
}) {
  const formatOptions = useMemo(() => (dateFnsLocale ? {locale: dateFnsLocale} : undefined), [dateFnsLocale]);

  // Le mois est en milieu de phrase : la locale date-fns décide seule de sa casse.
  const monthLabel = useMemo(() => format(currentMonth, 'MMMM', formatOptions), [currentMonth, formatOptions]);

  const dates = result?.dates || [];
  const horizon = result?.horizon || null;

  if (status === NEXT_AVAILABILITY_STATUS.IDLE || status === NEXT_AVAILABILITY_STATUS.ERROR) return null;
  if (status === NEXT_AVAILABILITY_STATUS.READY && horizon?.closed) return null;

  const isLoading = status === NEXT_AVAILABILITY_STATUS.LOADING;
  const hasDates = dates.length > 0;
  if (isLoading) return null;

  // Quand des places existent, le titre parle du mois affiché ; horizon épuisé,
  // il dit que tout le calendrier ouvert est pris, ou sur combien de mois le
  // serveur a cherché quand il a plafonné son balayage.
  const title = hasDates ?
    t(resolveFullMonthKey(service), {month: monthLabel, count: partySize}) :
    buildExhaustedTitle({horizon, t});
  if (!hasDates && !title) return null;

  return (
    <Fade in appear timeout={CONTENT_FADE_MS}>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          mb: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {hasDates ? (
          <Box sx={{textAlign: 'center'}}>
            <Typography variant="body2" sx={{fontWeight: 600, lineHeight: 1.4, color: 'text.primary'}}>
              {title}
            </Typography>
            <Box sx={{...EYEBROW_SX, mt: 1.25, mb: 1}}>
              {t('widgets.reservation.nextAvailability.nextPlaces')}
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75}}>
              {dates.map((entry) => (
                <DateCard key={entry.date} dateStr={entry.date} formatOptions={formatOptions} onPick={onPickDate} />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'}}>
            <Box sx={{...EYEBROW_SX, width: '100%', mb: 0.75}}>
              <EventBusyOutlinedIcon sx={{fontSize: EMPTY_STATE_ICON_SIZE}} />
            </Box>
            <Typography
              variant="body2"
              sx={{fontWeight: 600, lineHeight: 1.4, color: 'text.primary', whiteSpace: 'pre-line'}}
            >
              {title}
            </Typography>
            {horizon?.nextOpening && (
              <NextOpening nextOpening={horizon.nextOpening} formatOptions={formatOptions} t={t} />
            )}
          </Box>
        )}
      </Box>
    </Fade>
  );
}

/**
 * Clé du titre « mois complet » selon le service : midi, soir, ou neutre pour
 * un service au nom libre (brunch, goûter...) qui n'est ni l'un ni l'autre.
 *
 * @param {string} [service] - Nom du service sélectionné
 * @return {string} Clé de traduction
 */
function resolveFullMonthKey(service) {
  if (service === SERVICE_TYPES.LUNCH) return 'widgets.reservation.nextAvailability.fullMonthLunch';
  if (service === SERVICE_TYPES.DINNER) return 'widgets.reservation.nextAvailability.fullMonthDinner';
  return 'widgets.reservation.nextAvailability.fullMonth';
}

/**
 * Titre du bandeau quand aucune place n'a été trouvée.
 *
 * @param {Object} params
 * @param {Object} [params.horizon] - État de l'horizon renvoyé par le serveur
 * @param {Function} params.t - Fonction de traduction générique
 * @return {string|null} Titre traduit, ou null sans horizon
 */
function buildExhaustedTitle({horizon, t}) {
  if (!horizon?.lastScannedDate) return null;

  if (!horizon.capped) {
    return t('widgets.reservation.nextAvailability.fullAllOpenDates');
  }

  const scannedDays = differenceInCalendarDays(parseISO(horizon.lastScannedDate), startOfDay(new Date()));
  const months = Math.max(1, Math.round(scannedDays / DAYS_PER_MONTH));
  return t('widgets.reservation.nextAvailability.fullWithinMonths', {count: months});
}

/**
 * Prochaine date qui s'ouvrira à la réservation, et à partir de quand. Jour
 * et heure viennent du serveur en chaînes du fuseau de l'établissement, jamais
 * recalculés en local ; l'heure et le « dans N jours » reprennent les clés de
 * l'infobulle du calendrier.
 *
 * @param {Object} props
 * @param {Object} props.nextOpening - `{date, opensAtDate, opensAtTime}`
 * @param {Object} [props.formatOptions] - Options date-fns (locale)
 * @param {Function} props.t - Fonction de traduction générique
 * @return {JSX.Element}
 */
function NextOpening({nextOpening, formatOptions, t}) {
  const openingDay = parseISO(nextOpening.opensAtDate);
  const targetLabel = format(parseISO(nextOpening.date), 'EEEE d MMMM', formatOptions);
  const openingLabel = format(openingDay, 'EEEE d MMMM', formatOptions) +
    (nextOpening.opensAtTime ? ` ${t('widgets.common.bookingOpeningAtTime', {time: nextOpening.opensAtTime})}` : '');
  const daysUntil = differenceInCalendarDays(openingDay, startOfDay(new Date()));

  return (
    <Box sx={{mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider', width: '100%'}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: 'text.secondary'}}>
        <ScheduleRoundedIcon sx={{fontSize: 13}} />
        <Box component="span" sx={{typography: 'micro', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1}}>
          {t('widgets.reservation.nextAvailability.nextOpeningTitle')}
        </Box>
      </Box>
      <Typography variant="body2" sx={{mt: 0.5, fontWeight: 700, lineHeight: 1.4, color: 'text.primary'}}>
        {targetLabel.charAt(0).toUpperCase() + targetLabel.slice(1)}
      </Typography>
      <Typography variant="caption" component="p" sx={{mt: 0.75, lineHeight: 1.4, color: 'text.secondary'}}>
        {t('widgets.common.bookingOpeningTitle')}
      </Typography>
      <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.75}}>
        <Typography variant="body2" sx={{fontWeight: 600, lineHeight: 1.4, color: 'text.primary'}}>
          {openingLabel.charAt(0).toUpperCase() + openingLabel.slice(1)}
        </Typography>
        {daysUntil >= 1 && (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.125,
              borderRadius: 999,
              bgcolor: 'grey.100',
              color: 'text.secondary',
              typography: 'caption',
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {t('widgets.common.bookingOpensIn', {count: daysUntil})}
          </Box>
        )}
      </Box>
    </Box>
  );
}

NextOpening.propTypes = {
  nextOpening: PropTypes.shape({
    date: PropTypes.string.isRequired,
    opensAtDate: PropTypes.string.isRequired,
    opensAtTime: PropTypes.string,
  }).isRequired,
  formatOptions: PropTypes.object,
  t: PropTypes.func.isRequired,
};

/**
 * Carte de date : jour de la semaine, numéro en grand, mois. Libellés toujours
 * construits depuis la chaîne 'YYYY-MM-DD' du serveur (jour calendaire de
 * l'établissement), jamais depuis un Date fabriqué en local.
 *
 * @param {Object} props
 * @param {string} props.dateStr - Date 'YYYY-MM-DD'
 * @param {Object} [props.formatOptions] - Options date-fns (locale)
 * @param {Function} props.onPick - Reçoit la date 'YYYY-MM-DD' au clic
 * @return {JSX.Element}
 */
function DateCard({dateStr, formatOptions, onPick}) {
  const day = parseISO(dateStr);
  return (
    <ButtonBase
      focusRipple
      onClick={() => onPick(dateStr)}
      aria-label={format(day, LONG_DATE_FORMAT, formatOptions)}
      sx={{
        ...INTERACTIVE_SX,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: DATE_CARD_WIDTH,
        height: DATE_CARD_HEIGHT,
      }}
    >
      <Box
        component="span"
        sx={{typography: 'micro', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1.1}}
      >
        {format(day, 'EEE', formatOptions).replace('.', '')}
      </Box>
      <Box component="span" sx={{typography: 'subtitle1', fontWeight: 700, lineHeight: 1.1, my: 0.125}}>
        {format(day, 'd', formatOptions)}
      </Box>
      <Box component="span" sx={{typography: 'micro', fontWeight: 500, color: 'text.secondary', lineHeight: 1.1}}>
        {format(day, 'MMM', formatOptions).replace('.', '')}
      </Box>
    </ButtonBase>
  );
}

DateCard.propTypes = {
  dateStr: PropTypes.string.isRequired,
  formatOptions: PropTypes.object,
  onPick: PropTypes.func.isRequired,
};

NextAvailabilityBanner.propTypes = {
  status: PropTypes.oneOf(Object.values(NEXT_AVAILABILITY_STATUS)).isRequired,
  result: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.shape({date: PropTypes.string.isRequired})),
    horizon: PropTypes.shape({
      lastScannedDate: PropTypes.string,
      exhausted: PropTypes.bool,
      closed: PropTypes.bool,
      capped: PropTypes.bool,
      nextOpening: PropTypes.shape({
        date: PropTypes.string,
        opensAtDate: PropTypes.string,
        opensAtTime: PropTypes.string,
      }),
    }),
  }),
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  service: PropTypes.string.isRequired,
  partySize: PropTypes.number.isRequired,
  onPickDate: PropTypes.func.isRequired,
  dateFnsLocale: PropTypes.object,
  t: PropTypes.func.isRequired,
};
